import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, HardHat, LogOut, LayoutDashboard, User as UserIcon, MessageSquare, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AuthModal from "@/components/AuthModal";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_build-hub-1023/artifacts/0xoh0nsf_logo%20ETI.jpeg";

const NAV = [
  { label: "Home", to: "/" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Marketplace", to: "/marketplace" },
  { label: "Real Estate", to: "/real-estate" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const openAuth = (mode) => { setAuthMode(mode); setAuthOpen(true); setOpen(false); };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 eti-glass" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 lg:h-[70px]">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo-link">
            <span className="bg-white rounded-md px-2 py-1 flex items-center">
              <img src={LOGO} alt="Exclusive Trades Inc" className="h-6 sm:h-7 w-auto" />
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${location.pathname === n.to ? "text-primary" : "text-slate-300 hover:text-white"}`}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2" data-testid="navbar-user-menu">
                    <Avatar className="h-9 w-9 border border-white/15">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback className="bg-primary/20 text-primary text-sm">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-card border-white/10">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                    <p className="text-xs text-slate-400 capitalize">{user.role}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><UserIcon className="h-4 w-4 mr-2" />My Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages"><MessageSquare className="h-4 w-4 mr-2" />Messages</DropdownMenuItem>
                  {user.is_admin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="h-4 w-4 mr-2" />Admin Panel</DropdownMenuItem>}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-400"><LogOut className="h-4 w-4 mr-2" />Log Out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" className="text-slate-200" onClick={() => openAuth("login")} data-testid="navbar-login-button">Log In</Button>
                <Button className="bg-primary hover:bg-primary/90 text-white font-semibold" onClick={() => openAuth("register")} data-testid="navbar-signup-button">Join Free</Button>
              </>
            )}
          </div>

          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="navbar-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#0b0d10] px-4 py-4 space-y-1" data-testid="mobile-menu">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-slate-200 rounded-md hover:bg-white/5"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>{n.label}</Link>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              {user ? (
                <>
                  <Button variant="outline" className="border-white/15" onClick={() => { navigate("/dashboard"); setOpen(false); }} data-testid="mobile-dashboard">Dashboard</Button>
                  <Button variant="outline" className="border-white/15" onClick={() => { navigate("/profile"); setOpen(false); }}>My Profile</Button>
                  <Button variant="outline" className="border-white/15" onClick={() => { navigate("/messages"); setOpen(false); }}>Messages</Button>
                  {user.is_admin && <Button variant="outline" className="border-white/15" onClick={() => { navigate("/admin"); setOpen(false); }}>Admin Panel</Button>}
                  <Button variant="ghost" className="text-red-400" onClick={() => { logout(); setOpen(false); }}>Log Out</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="border-white/15 text-white" onClick={() => openAuth("login")}>Log In</Button>
                  <Button className="bg-primary text-white" onClick={() => openAuth("register")}>Join Free</Button>
                </>
              )}
            </div>
          </div>
        )}
      </header>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} mode={authMode} setMode={setAuthMode} />
    </>
  );
};

export default Navbar;
